import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { Modal } from '../Modal/Modal';
import './ConfirmationModal.scss';

/**
 * ConfirmationModal Component
 *
 * A reusable modal component for displaying confirmation dialogs, info messages,
 * error messages, and success messages with appropriate icons and button variants.
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function called when modal is closed
 * @param {function} onConfirm - Function called when confirm button is clicked
 * @param {string} type - Type of modal: 'info', 'error', 'success', 'warning'
 * @param {string} title - Modal title
 * @param {string} message - Modal message/content
 * @param {string} confirmText - Text for confirm button (optional)
 * @param {string} cancelText - Text for cancel button (optional)
 * @param {boolean} showCancel - Whether to show cancel button (default: true)
 * @param {string} size - Modal size: 'sm', 'md', 'lg' (default: 'md')
 * @param {boolean} closeOnOverlayClick - Whether clicking overlay closes modal (default: false)
 *
 * @example
 * // Delete confirmation
 * <ConfirmationModal
 *   isOpen={isDeleteModalOpen}
 *   onClose={() => setIsDeleteModalOpen(false)}
 *   onConfirm={handleDelete}
 *   type="delete"
 *   title="Delete Milestone"
 *   message="Are you sure you want to delete this milestone? This action cannot be undone."
 *   confirmText="Delete"
 *   cancelText="Cancel"
 * />
 *
 * @example
 * // Success message
 * <ConfirmationModal
 *   isOpen={isSuccessOpen}
 *   onClose={() => setIsSuccessOpen(false)}
 *   type="success"
 *   title="Success!"
 *   message="Milestone has been created successfully."
 *   confirmText="OK"
 *   showCancel={false}
 * />
 */

export const ConfirmationModal = ({
  isOpen = false,
  onClose,
  onConfirm,
  type = 'info',
  title,
  message,
  confirmText,
  cancelText,
  showCancel = true,
  size = 'md',
  closeOnOverlayClick = false,
}) => {
  // Memoized modal configuration based on type
  const modalConfig = useMemo(() => {
    const configs = {
      info: {
        icon: 'Info',
        iconColor: 'info',
        confirmVariant: 'primary',
        confirmText: confirmText || 'OK',
        cancelText: cancelText || 'Close',
        showCancel: showCancel !== false,
      },
      success: {
        icon: 'Check_Circle',
        iconColor: 'success',
        confirmVariant: 'success',
        confirmText: confirmText || 'OK',
        cancelText: cancelText || 'Close',
        showCancel: showCancel !== false,
      },
      error: {
        icon: 'Trash_Empty',
        iconColor: 'error',
        confirmVariant: 'danger',
        confirmText: confirmText || 'OK',
        cancelText: cancelText || 'Close',
        showCancel: showCancel !== false,
      },
      warning: {
        icon: 'Warning',
        iconColor: 'warning',
        confirmVariant: 'warning',
        confirmText: confirmText || 'Continue',
        cancelText: cancelText || 'Cancel',
        showCancel: showCancel !== false,
      },
    };

    return configs[type] || configs.info;
  }, [type, confirmText, cancelText, showCancel]);

  // Handle confirm action
  const handleConfirm = () => {
    onConfirm?.();
    if (type !== 'info' && type !== 'success' && type !== 'error') {
      onClose?.();
    }
  };

  // Handle cancel action
  const handleCancel = () => {
    onClose?.();
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      closeOnOverlayClick={closeOnOverlayClick}
      showFooter={false}
    >
      <div className='confirmation-modal'>
        {/* Icon */}
        <div
          className={`confirmation-modal__icon confirmation-modal__icon--${modalConfig.iconColor}`}
        >
          <Icon name={modalConfig.icon} size='md' />
        </div>

        {/* Content */}
        <div className='confirmation-modal__content'>
          {title && <h3 className='confirmation-modal__title'>{title}</h3>}

          {message && (
            <div className='confirmation-modal__message'>{message}</div>
          )}
        </div>

        {/* Actions */}
        <div className='confirmation-modal__actions'>
          {modalConfig.showCancel && (
            <Button
              variant='secondary-outline'
              onClick={handleCancel}
              size='md'
            >
              {modalConfig.cancelText}
            </Button>
          )}

          <Button
            variant={modalConfig.confirmVariant}
            onClick={handleConfirm}
            size='md'
          >
            {modalConfig.confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  type: PropTypes.oneOf(['info', 'error', 'success', 'warning']),
  title: PropTypes.string,
  message: PropTypes.node.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  showCancel: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  closeOnOverlayClick: PropTypes.bool,
};
